import React, { useRef, useEffect } from "react";
import {
  Animated,
  Linking,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Image } from "native-base";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { Feather, FontAwesome5 } from "@expo/vector-icons";
import styles from "./styles";

import ModalConfirmacao from "../../../../components/ModalConfirmacao";
import imageNaoEncontrada from "../../../img/nao_encontrado.png";
import packageJson from "../../../../package.json";
import SkeletonInicio from "../../../components/Skeleton/Inicio";
import Alerta from "../../../components/Alerta/Index";
import Header from "./Header/Index";
import {
  corComAlpha,
  corTextoSobre,
  gradienteCard,
  gradienteHero,
} from "../../../theme/cores";

interface Layout3Props {
  dados_empresa: any; // Substitua 'any' pelo tipo correto
  empresaNaoEncontrada: boolean;
  exibeBotaoAgendarPacote: boolean;
  isMobile: boolean;
  colorMode: any; // ou o tipo específico se houver
  primaryColor: any; // ou o tipo específico se houver
}

const DOURADO = "#E3BF7A";

const Layout3: React.FC<Layout3Props> = ({
  dados_empresa,
  empresaNaoEncontrada,
  exibeBotaoAgendarPacote,
  isMobile,
  colorMode,
  primaryColor,
}) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const refModal = useRef<any>(null);

  const corPrincipal: string =
    primaryColor || dados_empresa?.corPrincipalEmpresa || "#006400";
  const escuro = colorMode == "dark";

  const tema = {
    fundo: escuro ? "#0f0f0f" : "#F4F0E8",
    card: escuro ? "#1c1c1c" : "#ffffff",
    borda: escuro ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
    titulo: escuro ? "#ffffff" : "#1d1d1d",
    subtitulo: escuro ? "#a8a8a8" : "#7c7c7c",
    destaque: escuro ? "#ffffff" : corPrincipal,
  };

  const corTextoPrincipal = corTextoSobre(corPrincipal);

  const shakeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.sequence([
      Animated.timing(shakeAnim, {
        toValue: -6,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(shakeAnim, {
        toValue: 6,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(shakeAnim, {
        toValue: -6,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(shakeAnim, {
        toValue: 0,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  function abrirUrl(url: string) {
    if (!url) return;
    Linking.openURL(url);
  }

  function cardAcao(params: {
    titulo: string;
    subtitulo: string;
    icone: React.ReactNode;
    iconeDestacado: boolean;
    onPress: () => void;
  }) {
    return (
      <TouchableOpacity
        activeOpacity={0.85}
        style={[
          styles.cardAcao,
          { backgroundColor: tema.card, borderColor: tema.borda },
        ]}
        onPress={params.onPress}
      >
        <View
          style={[
            styles.iconeCardAcao,
            {
              backgroundColor: params.iconeDestacado
                ? corPrincipal
                : corComAlpha(corPrincipal, escuro ? 0.3 : 0.1),
            },
          ]}
        >
          {params.icone}
        </View>

        <View style={{ flex: 1 }}>
          <Text style={[styles.tituloCardAcao, { color: tema.titulo }]}>
            {params.titulo}
          </Text>
          <Text style={[styles.subtituloCardAcao, { color: tema.subtitulo }]}>
            {params.subtitulo}
          </Text>
        </View>

        <Feather
          name="chevron-right"
          size={22}
          color={params.iconeDestacado ? tema.destaque : tema.subtitulo}
        />
      </TouchableOpacity>
    );
  }

  function boxBotaoAgendar() {
    return cardAcao({
      titulo: "Agendamentos",
      subtitulo: "Reserve seu horário em segundos",
      iconeDestacado: true,
      icone: <Feather name="calendar" size={22} color={corTextoPrincipal} />,
      onPress: () => {
        navigation.navigate("InicioStack", {
          screen: "Profissional",
          params: {
            id_empresa: dados_empresa.id,
          },
        });
      },
    });
  }

  function boxBotaoAgendarClienteFiel() {
    return cardAcao({
      titulo: "Já sou mensalista",
      subtitulo: "Agende usando seu plano",
      iconeDestacado: false,
      icone: <FontAwesome5 name="gem" size={20} color={tema.destaque} />,
      onPress: () => {
        navigation.navigate("TabNavigator", {
          screen: "Login",
          params: {
            id_empresa: dados_empresa.id,
          },
        });
      },
    });
  }

  function chipBeneficio(texto: string) {
    return (
      <View style={styles.chip}>
        <Feather name="check" size={13} color={DOURADO} />
        <Text style={styles.textoChip}>{texto}</Text>
      </View>
    );
  }

  function boxAssinaturas() {
    return (
      <Animated.View
        style={[styles.larguraConteudo, { transform: [{ translateX: shakeAnim }] }]}
      >
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={() => {
            navigation.navigate("InicioStack", {
              screen: "Assinaturas",
              params: {
                dados_empresa: dados_empresa,
              },
            });
          }}
        >
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 22,
              backgroundColor: corPrincipal,
              backgroundImage: gradienteCard(corPrincipal),
              boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
            }}
          >
            {/* círculos decorativos */}
            <div style={circuloDecorativo(150, -40, -40)} />
            <div style={circuloDecorativo(100, -15, -15)} />

            <View style={{ padding: 20 }}>
              <View style={styles.badgeClube}>
                <FontAwesome5 name="gem" size={11} color={DOURADO} />
                <Text style={styles.textoBadgeClube}>CLUBE DE ASSINATURA</Text>
              </View>

              <Text style={styles.tituloAssinatura}>
                Seu corte em dia,{"\n"}todo mês
              </Text>
              <Text style={styles.textoAssinatura}>
                Assine um plano e economize em cada visita.
              </Text>

              <View style={styles.linhaChips}>
                {chipBeneficio("Mais economia")}
                {chipBeneficio("Mais praticidade")}
              </View>
            </View>

            <View style={styles.rodapeAssinatura}>
              <Text style={styles.textoRodapeAssinatura}>
                Conhecer os planos
              </Text>
              <View style={styles.botaoSetaAssinatura}>
                <Feather name="arrow-right" size={16} color="#1d1d1d" />
              </View>
            </View>
          </div>
        </TouchableOpacity>
      </Animated.View>
    );
  }

  function boxMaps() {
    const linkMaps = dados_empresa.endereco
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          dados_empresa.endereco,
        )}`
      : "";

    return (
      <View
        style={[
          styles.cardLocalizacao,
          { backgroundColor: tema.card, borderColor: tema.borda },
        ]}
      >
        <View style={styles.cabecalhoLocalizacao}>
          <View style={styles.linhaCentralizada}>
            <Feather name="map-pin" size={16} color={tema.destaque} />
            <Text style={[styles.tituloLocalizacao, { color: tema.titulo }]}>
              Localização
            </Text>
          </View>

          {!!linkMaps && (
            <TouchableOpacity onPress={() => abrirUrl(linkMaps)}>
              <Text style={[styles.linkMapa, { color: tema.destaque }]}>
                Ver no mapa →
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.containerMapa}>
          <iframe
            src={dados_empresa.urlMaps}
            width="100%"
            height={isMobile ? "260" : "420"}
            style={{ border: 0, display: "block" }}
            loading="lazy"
          ></iframe>
        </View>

        {!!dados_empresa.endereco && (
          <Text style={[styles.textoEndereco, { color: tema.subtitulo }]}>
            {dados_empresa.endereco}
          </Text>
        )}
      </View>
    );
  }

  function boxRedesSociais() {
    const redes = [
      { icone: "instagram", url: dados_empresa.instagramurl },
      { icone: "facebook", url: dados_empresa.facebookurl },
    ].filter((rede) => !!rede.url);

    return (
      <div
        style={{
          width: "100%",
          boxSizing: "border-box",
          marginTop: 24,
          paddingTop: 28,
          paddingBottom: 90,
          paddingLeft: 20,
          paddingRight: 20,
          backgroundColor: corPrincipal,
          backgroundImage: gradienteHero(corPrincipal),
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {redes.length > 0 && (
          <>
            <Text style={[styles.tituloRodape, { color: corTextoPrincipal }]}>
              Junte-se a nós
            </Text>
            <Text
              style={[
                styles.subtituloRodape,
                { color: corTextoPrincipal, opacity: 0.8 },
              ]}
            >
              Siga nossas redes sociais
            </Text>

            <View style={styles.linhaRedes}>
              {redes.map((rede) => (
                <TouchableOpacity
                  key={rede.icone}
                  style={styles.botaoRede}
                  onPress={() => abrirUrl(rede.url)}
                >
                  <Feather
                    name={rede.icone as any}
                    size={22}
                    color={corTextoPrincipal}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}

        <Text
          style={[
            styles.textoVersao,
            { color: corTextoPrincipal, opacity: 0.6 },
          ]}
        >
          Versão: {packageJson.version}
        </Text>
      </div>
    );
  }

  return (
    <ScrollView style={{ backgroundColor: tema.fundo }}>
      {!dados_empresa.id && !empresaNaoEncontrada ? (
        <SkeletonInicio />
      ) : !empresaNaoEncontrada ? (
        <View style={[styles.container, { backgroundColor: tema.fundo }]}>
          <Header
            dados_empresa={dados_empresa}
            primaryColor={corPrincipal}
            colorMode={colorMode}
          />

          <View style={[styles.conteudo, styles.larguraConteudo]}>
            {boxBotaoAgendar()}

            {exibeBotaoAgendarPacote && boxBotaoAgendarClienteFiel()}
          </View>

          {dados_empresa.permiteVendaPacotePeloPwa && boxAssinaturas()}

          {!!dados_empresa.urlMaps && (
            <View style={styles.larguraConteudo}>{boxMaps()}</View>
          )}

          {boxRedesSociais()}
        </View>
      ) : (
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <Image
            source={imageNaoEncontrada}
            alt="Logo"
            resizeMode="cover"
            style={{
              width: 200,
              height: 200,
            }}
          />
          <Alerta
            status="warning"
            message="<b>Não foi possível encontrar a empresa</b>. <br /> Por favor, tente novamente mais tarde"
            messageHtml
          />
        </View>
      )}

      <ModalConfirmacao
        titulo={"Atenção"}
        ref={refModal}
        mensagem={""}
        textoBotaoPositivo={"OK"}
        onConfirmPositivo={() => {
          if (refModal.current) {
            refModal.current.fecharModal();
          }
        }}
      />
    </ScrollView>
  );
};

function circuloDecorativo(
  tamanho: number,
  top: number,
  right: number,
): React.CSSProperties {
  return {
    position: "absolute",
    width: tamanho,
    height: tamanho,
    top,
    right,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.12)",
    pointerEvents: "none",
  };
}

export default Layout3;
